import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { PrimaryButton, SecondaryButton } from "../../../UI";
import { Field, InfoTip, JsonEditor, LinkButton, optionsTip, Section, SelectInput, StatusMessage, TextInput, useStatus, WithInfo } from "../common";
import { ROLE_LABELS } from "../constants";
import { GLOSSARY } from "../glossary";
import { displayValue } from "../helpers";
import { autoMapSource, describeAutoMap, sameExtract } from "./autoMap";
import ExpressionEditor from "./ExpressionEditor";
import ResponseExplorer from "./ResponseExplorer";

const EXPRESSION_TIP = optionsTip(GLOSSARY.mapping.expression, GLOSSARY.mapping.modes, { path: "Path", const: "Fixed value", template: "Template", ref: "Context", all: "All true", any: "Any true", advanced: "Advanced (JSON)" });

const FIELD_TIP = optionsTip(GLOSSARY.fields.model, GLOSSARY.fields.roles, ROLE_LABELS);
const SAMPLE_ACTIONS_TIP = optionsTip(undefined, { load: GLOSSARY.mapping.loadSamples, auto: GLOSSARY.mapping.autoMap }, { load: "Load / refresh samples", auto: "Auto-map by role" });

// Roles a feed can't be published without.
const REQUIRED_ROLES = { product: ["key", "name"], variant: ["price"] };

// A clicked path in the bound context ($.variant.priceCondition.PVP[0].price)
// becomes a field path: unprefixed for the row's own scope, $binding.… otherwise.
const toFieldPath = (path, scope) => {
    const match = path.match(/^\$\.([A-Za-z_][A-Za-z0-9_]*)(.*)$/);
    if (!match) return path;
    const [, binding, rest] = match;
    if (binding === scope) return rest.replace(/^\./, "") || "@";
    return `$${binding}${rest}`;
};

const MappingTable = ({ title, level, fields, map, onChange, catalogue, samples, coverage, total, pick, onPick, highlight }) => (
    <Section title={title} description={`Paths without a prefix read from $${level}. Other bindings: $product, $variant, $root, $key and any named levels.`} tip={GLOSSARY.mapping.bindings}>
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="py-2 pr-2"><WithInfo tip={FIELD_TIP} wide>Field</WithInfo></th>
                        <th className="py-2 pr-2"><WithInfo tip={EXPRESSION_TIP} wide>Expression</WithInfo></th>
                        <th className="py-2 pr-2 whitespace-nowrap"><WithInfo tip={GLOSSARY.mapping.foundIn}>Found in</WithInfo></th>
                        <th className="py-2"><WithInfo tip={GLOSSARY.mapping.samples}>Sample values</WithInfo></th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field) => {
                        const active = pick?.level === level && pick?.key === field.key;
                        const autoMapped = highlight.has(`${level}.${field.key}`);
                        const missingRequired = REQUIRED_ROLES[level].includes(field.role) && !map?.[field.key];
                        return (
                            <tr key={field.key} className={`border-b border-gray-100 align-top transition-colors ${active ? "bg-blue-50" : autoMapped ? "bg-green-50" : ""}`}>
                                <td className="py-2 pr-2 whitespace-nowrap">
                                    <p className="font-mono">{field.key}</p>
                                    <p className="text-xs text-gray-500">
                                        {field.type}
                                        {field.role && <span className="ml-1 px-1.5 rounded bg-blue-100 text-blue-800">{ROLE_LABELS[field.role] || field.role}</span>}
                                    </p>
                                    {autoMapped && <p className="text-xs text-green-700"><WithInfo tip={GLOSSARY.mapping.autoMapped}>Auto-mapped</WithInfo></p>}
                                    {missingRequired && <p className="text-xs text-red-500"><WithInfo tip={GLOSSARY.mapping.needsMapping}>Needs a mapping</WithInfo></p>}
                                </td>
                                <td className="py-2 pr-2">
                                    <ExpressionEditor
                                        value={map?.[field.key]}
                                        catalogue={catalogue}
                                        pickActive={active}
                                        onPick={() => onPick(level, field.key)}
                                        onChange={(value) => onChange(field.key, value)}
                                    />
                                </td>
                                <td className="py-2 pr-2 text-xs text-gray-500 whitespace-nowrap">
                                    {coverage ? `${coverage[field.key] || 0} / ${total}` : "—"}
                                </td>
                                <td className="py-2 text-xs font-mono text-gray-700 max-w-[320px]">
                                    {(samples || []).map((sample, i) => (
                                        <p key={i} className="truncate" title={displayValue(sample[field.key])}>{displayValue(sample[field.key]) || <span className="text-gray-300">empty</span>}</p>
                                    ))}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </Section>
);

// Step 3: map the source onto the feed model.
const MappingStep = ({ source, update, draft, meta, testResult, extractResult, extracting, runExtract, onModelChange }) => {
    const [pick, setPick] = useState(null);
    const [highlight, setHighlight] = useState(() => new Set());
    const [mapStatus, showMapStatus] = useStatus(15000);
    const [pickStatus, showPickStatus] = useStatus(6000);
    const [fieldStatus, showFieldStatus] = useStatus(8000);
    const [contextIndex, setContextIndex] = useState(0);
    const [newField, setNewField] = useState({ level: "variant_fields", key: "", type: "string" });
    const productFields = draft.model?.product_fields || [];
    const variantFields = draft.model?.variant_fields || [];
    const catalogue = meta?.transforms || [];

    const records = extractResult?.records || [];
    const productSamples = records.slice(0, 3).map((r) => r.fields);
    const variantSamples = records.flatMap((r) => r.variants).slice(0, 3).map((v) => v.fields);
    const contexts = extractResult?.contexts || [];
    const context = contexts[contextIndex]?.bindings;

    const setMapping = (mapName) => (key, value) => {
        // An edited row is no longer "auto-mapped".
        const level = mapName === "product_map" ? "product" : "variant";
        if (highlight.has(`${level}.${key}`)) setHighlight(new Set([...highlight].filter((k) => k !== `${level}.${key}`)));
        update((s) => {
            const next = { ...(s[mapName] || {}) };
            if (value === undefined || value === "") delete next[key];
            else next[key] = value;
            return { ...s, [mapName]: next };
        });
    };

    // Role hints are relative to the suggested product and variant lists, so
    // they only apply while the extraction matches that suggestion.
    const suggestion = useMemo(() => {
        const suggestions = testResult?.detection?.suggestions || [];
        return suggestions.find((s) => sameExtract(s.extract, source.extract));
    }, [testResult, source.extract]);

    const autoMap = async () => {
        if (!testResult) {
            showMapStatus("Test the request in step 1 first: auto-map uses the structure detected in the response.", "error");
            return;
        }
        if (!suggestion) {
            showMapStatus("Auto-map works with a suggested structure. Choose one in step 2 (or undo manual changes to the extraction), then try again.", "error");
            return;
        }
        const { source: next, mapped } = autoMapSource(source, draft.model, suggestion);
        if (!mapped.length) {
            showMapStatus(describeAutoMap(mapped), "info");
            return;
        }
        update(next);
        setHighlight(new Set(mapped));
        showMapStatus(`${describeAutoMap(mapped)} Refreshing samples...`, "info");
        const { error } = await runExtract(next);
        showMapStatus(
            error ? `${describeAutoMap(mapped)} The samples couldn't be refreshed: ${error}` : `${describeAutoMap(mapped)} Mapped rows are highlighted.`,
            error ? "error" : "success"
        );
    };

    const loadSamples = async () => {
        showMapStatus("Loading samples...", "info");
        const { data, error } = await runExtract();
        if (error) showMapStatus(`Couldn't load samples: ${error}`, "error");
        else showMapStatus(`Loaded ${data?.totals?.products ?? 0} products and ${data?.totals?.variants ?? 0} variants.`);
    };

    const startPick = (level, key) => {
        if (!context) {
            showPickStatus("Load samples first: values are picked from a sample product.", "error");
            return;
        }
        setPick(pick?.level === level && pick?.key === key ? null : { level, key });
    };

    const onPickNode = (path) => {
        if (!pick) return;
        const scope = pick.level === "product" ? "product" : "variant";
        const mapName = pick.level === "product" ? "product_map" : "variant_map";
        const fieldPath = toFieldPath(path, scope);
        setMapping(mapName)(pick.key, fieldPath);
        showPickStatus(`Mapped ${pick.level} field "${pick.key}" to ${fieldPath}. Refresh samples to see its values.`);
        setPick(null);
    };

    const addField = () => {
        const key = newField.key.trim();
        const level = newField.level === "product_fields" ? "product" : "variant";
        if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) {
            showFieldStatus("Field names start with a letter or _ and use only letters, digits and _.", "error");
            return;
        }
        const list = draft.model?.[newField.level] || [];
        if (list.some((f) => f.key === key)) {
            showFieldStatus(`There's already a ${level} field called "${key}".`, "error");
            return;
        }
        onModelChange({ ...draft.model, [newField.level]: [...list, { key, type: newField.type }] });
        setNewField({ ...newField, key: "" });
        showFieldStatus(`Added ${level} field "${key}" to the feed model. Map it in the table above; set a role or merge policy in the Fields tab.`);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
            <div className="flex flex-col gap-4 min-w-0">
                {pick && (
                    <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                        <span>Click a value in the sample context to map {pick.level} field "{pick.key}".</span>
                        <LinkButton onClick={() => setPick(null)}>Cancel</LinkButton>
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                    <PrimaryButton size="sm" loading={extracting} handleClick={loadSamples}>
                        {extractResult && !extractResult.error ? "Refresh samples" : "Load samples"}
                    </PrimaryButton>
                    <SecondaryButton size="sm" disable={extracting} handleClick={autoMap}>Auto-map by role</SecondaryButton>
                    <InfoTip content={SAMPLE_ACTIONS_TIP} wide />
                    {extractResult?.totals && (
                        <span className="text-xs text-gray-500">
                            {extractResult.totals.products} products · {extractResult.totals.variants} variants
                            {Object.keys(extractResult.stats?.type_errors || {}).length > 0 && (
                                <>
                                    {" · "}
                                    <WithInfo tip={GLOSSARY.mapping.typeErrors}>type errors</WithInfo>: {Object.entries(extractResult.stats.type_errors).map(([k, v]) => `${k} (${v})`).join(", ")}
                                </>
                            )}
                        </span>
                    )}
                    {extractResult?.error && !mapStatus && <span className="text-sm text-red-500">{extractResult.error}</span>}
                </div>
                <StatusMessage status={mapStatus} />
                <StatusMessage status={pickStatus} />

                <MappingTable
                    title="Product fields"
                    level="product"
                    fields={productFields}
                    map={source.product_map}
                    onChange={setMapping("product_map")}
                    catalogue={catalogue}
                    samples={productSamples}
                    coverage={extractResult?.coverage?.product}
                    total={extractResult?.totals?.products}
                    pick={pick}
                    onPick={startPick}
                    highlight={highlight}
                />
                <MappingTable
                    title="Variant fields"
                    level="variant"
                    fields={variantFields}
                    map={source.variant_map}
                    onChange={setMapping("variant_map")}
                    catalogue={catalogue}
                    samples={variantSamples}
                    coverage={extractResult?.coverage?.variant}
                    total={extractResult?.totals?.variants}
                    pick={pick}
                    onPick={startPick}
                    highlight={highlight}
                />

                <Section title="Add a model field" description="Fields are shared by all sources; set roles and merge policies in the Fields tab." tip={GLOSSARY.mapping.addField}>
                    <div className="flex flex-wrap gap-2 items-end">
                        <Field label="Level" tip="Product fields hold one value per product; variant fields hold one value per variant.">
                            <SelectInput value={newField.level} options={[{ value: "product_fields", label: "Product" }, { value: "variant_fields", label: "Variant" }]} onChange={(level) => setNewField({ ...newField, level })} />
                        </Field>
                        <Field label="Name">
                            <TextInput value={newField.key} placeholder="storage" onChange={(key) => setNewField({ ...newField, key })} />
                        </Field>
                        <Field label="Type" tip={optionsTip("Values are converted to this type when they are mapped.", GLOSSARY.fields.types)}>
                            <SelectInput value={newField.type} options={meta?.field_types || ["string"]} onChange={(type) => setNewField({ ...newField, type })} />
                        </Field>
                        <LinkButton className="mb-2" onClick={addField}><Plus size={14} /> Add field</LinkButton>
                    </div>
                    <StatusMessage status={fieldStatus} />
                </Section>

                <details>
                    <summary className="text-sm font-medium text-primary cursor-pointer">Edit mappings as JSON</summary>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                        <Field label="product_map"><JsonEditor rows={12} value={source.product_map || {}} onChange={(v) => update((s) => ({ ...s, product_map: v || {} }))} /></Field>
                        <Field label="variant_map"><JsonEditor rows={12} value={source.variant_map || {}} onChange={(v) => update((s) => ({ ...s, variant_map: v || {} }))} /></Field>
                    </div>
                </details>
            </div>

            <div className="min-w-0">
                <Section
                    title="Sample context"
                    tip={GLOSSARY.mapping.context}
                    description={pick ? `Click a value to map ${pick.level} field "${pick.key}".` : "Use a field's pick button, then click a value."}
                    actions={contexts.length > 1 && (
                        <SelectInput className="!py-1 text-xs" value={String(contextIndex)} options={contexts.map((c, i) => ({ value: String(i), label: `Product ${i + 1}` }))} onChange={(v) => setContextIndex(Number(v))} />
                    )}
                >
                    {context ? (
                        <ResponseExplorer sample={context} onPick={onPickNode} pickLabel="Top-level names are bindings ($product, $variant and named levels)." />
                    ) : (
                        <p className="text-sm text-gray-500">Load samples to see the data each field can read.</p>
                    )}
                </Section>
            </div>
        </div>
    );
};

export default MappingStep;
